import type { DialogProps as MaterialDialogProps } from 'material-ui'
import * as sentry from '../../src/utils/sentry'
import { type Auth_State, Auth_initialState } from '../Auth/Auth.State'
import type { Cust_TServerPrefs } from '../Cust/Cust.serverPrefs'
import type { FeatureFlag } from '../../../shared/domain/status/types.no-deps'
import { Logg_event } from '../Logg/Logg.event'
import type { Post_TPostId } from '../Post/Post.TPost'
import type { User_IUser } from '../User/User.IUser'
import type { Nullable, UUID } from '../../../shared/lib/typeHelpers.no-deps'
import { Subscriber } from '../../../shared/lib/Subscriber.no-deps'
import type { AiChatUserContext } from '../../../shared/domain/aiChats/types.no-deps'

// MUTABLE
const Data: DS_TData = DS_getInitialData()
const subscriber = new Subscriber<void>()

export type DS_TData = {
    accessToken: string
    appCenterMetricsDisabled: boolean
    auth: Auth_State
    cleanse: { status: 'ready' | 'in-progress' } | { status: 'failure'; error: Error }
    customer: {
        id: number
        name: string
        slug: string
        domainName: string
    }
    dialog: {
        props: MaterialDialogProps
        content: React.ReactNode
    }
    featureFlags: FeatureFlag[]
    forcedLogoutCanLogoutWithoutWarning: boolean
    forcedLogoutCount: number
    idbConnectionStatus: 'open' | 'closed'
    languageOverrides: { [path: string]: string }
    lastSyncTime: number
    menuLeftIsOpen: boolean
    menuRightIsOpen: boolean
    unreadNotifications: UUID[]
    pathname: string
    postToScrollTo: Post_TPostId
    onLoad: { status: 'loading' | 'success' } | { status: 'failure'; error: Error }
    safeLogout: { status: 'ready' | 'in-progress' } | { status: 'failure'; error: Error }
    screenWidth: number
    serverPrefs: Cust_TServerPrefs
    slugs: { [slug: string]: string }
    snackbarMessages: string[]
    snackbarIsSwitching: boolean
    syncInterval: number
    todoSyncTime: number
    scheduledInstancePruneTime: number
    activeUnitIds: Set<number>
    user: User_IUser
    shouldResync: boolean
    zapBotContext: Nullable<AiChatUserContext>
}

export function DS_getInitialData(): DS_TData {
    return {
        accessToken: null,
        appCenterMetricsDisabled: false,
        auth: Auth_initialState,
        cleanse: { status: 'ready' },
        customer: null,
        dialog: {
            props: { open: false },
            content: null,
        },
        featureFlags: [],
        forcedLogoutCanLogoutWithoutWarning: false,
        forcedLogoutCount: 0,
        idbConnectionStatus: 'closed',
        languageOverrides: {},
        lastSyncTime: 0,
        menuLeftIsOpen: false,
        menuRightIsOpen: false,
        unreadNotifications: [],
        onLoad: { status: 'loading' },
        safeLogout: { status: 'ready' },
        pathname: location.pathname,
        postToScrollTo: null,
        screenWidth: window.screen.width,
        serverPrefs: {},
        slugs: {},
        snackbarMessages: [],
        snackbarIsSwitching: false,
        syncInterval: 60000,
        todoSyncTime: 0,
        scheduledInstancePruneTime: 0,
        activeUnitIds: new Set(),
        user: null,
        shouldResync: false,
        zapBotContext: null,
    }
}

export function DS_subscribe(handler: () => void) {
    subscriber.subscribe(handler)
}

export function DS_unsubscribe(handler: () => void) {
    subscriber.unsubscribe(handler, sentry.simpleError)
}

export function DS_getData() {
    return Data
}

export function DS_setData(label: string, getChanges: (Data: DS_TData) => Partial<DS_TData>) {
    // Logging
    Logg_event('DataSet', { label })

    // Mutate the actual global data in place
    Object.assign(Data, getChanges(Data))

    // Derived data can go here

    // Invariant checks should go here
    subscriber.dispatch(null, sentry.simpleError)
}
